import type { ReactNode } from "react";

import { SortableTableHead, type SortDir } from "@/components/sortable-table-head";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { AdminActivityLogEntry } from "@/lib/api";
import { formatActionLabel, formatDateTime, formatDetail } from "@/lib/format";

export type { SortDir };

interface ActivityTableProps {
  entries: AdminActivityLogEntry[];
  searchTerm: string;
  sortBy: string;
  sortDir: SortDir;
  onSort: (col: string) => void;
  loading?: boolean;
}

/** Wrap every case-insensitive match of `term` in <mark> for search highlight. */
function highlight(text: string, term: string): ReactNode {
  if (!term) {
    return text;
  }
  const lower = text.toLowerCase();
  const q = term.toLowerCase();
  const parts: ReactNode[] = [];
  let i = 0;
  let key = 0;
  for (let next = lower.indexOf(q); next !== -1; next = lower.indexOf(q, i)) {
    parts.push(text.slice(i, next));
    parts.push(
      <mark key={key++} className="bg-accent/30 text-foreground rounded px-0.5">
        {text.slice(next, next + term.length)}
      </mark>,
    );
    i = next + term.length;
  }
  parts.push(text.slice(i));
  return <>{parts}</>;
}

const COLUMNS: { key: string; label: string; sortable: boolean; right?: boolean }[] = [
  { key: "email", label: "User", sortable: true },
  { key: "action", label: "Action", sortable: true },
  { key: "detail", label: "Detail", sortable: false },
  { key: "created_at", label: "When", sortable: true, right: true },
];

export function ActivityTable({
  entries,
  searchTerm,
  sortBy,
  sortDir,
  onSort,
  loading,
}: ActivityTableProps) {
  return (
    <div className="border-border bg-card relative overflow-x-auto rounded-lg border">
      {loading && (
        <div className="bg-background/60 absolute inset-0 z-10 flex items-center justify-center">
          <span className="text-muted text-xs">Loading...</span>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            {COLUMNS.map((col) =>
              col.sortable ? (
                <SortableTableHead
                  key={col.key}
                  column={col.key}
                  label={col.label}
                  align={col.right ? "right" : "left"}
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
              ) : (
                <TableHead key={col.key} className={col.right ? "text-right" : undefined}>
                  {col.label}
                </TableHead>
              ),
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COLUMNS.length} className="text-muted py-8 text-center">
                No activity found.
              </TableCell>
            </TableRow>
          ) : (
            entries.map((entry) => {
              const name = (entry.full_name ?? "").trim();
              const identifier = entry.email ?? entry.user_id;
              const detailStr = formatDetail(entry.detail);
              return (
                <TableRow key={entry.id}>
                  <TableCell>
                    <div className="text-foreground font-medium">
                      {name ? highlight(name, searchTerm) : "—"}
                    </div>
                    <div className="text-muted text-xs">{highlight(identifier, searchTerm)}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="font-medium">
                      {highlight(formatActionLabel(entry.action), searchTerm)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted max-w-xs truncate text-sm">
                    {detailStr ? highlight(detailStr, searchTerm) : "—"}
                  </TableCell>
                  <TableCell className="text-muted text-right text-sm whitespace-nowrap">
                    {formatDateTime(entry.created_at)}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
